import api from "../utils/axiosInstance";

const QUOTATIONS_PATH = "/quotations";

export function buildQuotationHistoryParams(filters = {}) {
  const params = {};
  [
    "search",
    "quotation_type",
    "quotation_number",
    "customer_name",
    "reference",
    "service_cost_min",
    "service_cost_max",
    "created_by",
    "date_from",
    "date_to",
    "date_exact",
    "sort_by",
    "sort_order",
    "branch",
  ].forEach(
    (key) => {
      const value = String(filters[key] ?? "").trim();
      if (value) params[key] = value;
    }
  );
  const page = Number(filters.page);
  const pageSize = Number(filters.page_size);
  if (Number.isInteger(page) && page >= 1) params.page = page;
  if (Number.isInteger(pageSize) && pageSize >= 1 && pageSize <= 100) {
    params.page_size = pageSize;
  }
  return params;
}

export async function listQuotationHistory(filters, { signal } = {}) {
  const { data } = await api.get(QUOTATIONS_PATH, {
    params: buildQuotationHistoryParams(filters),
    signal,
  });
  return data;
}

export async function listQuotationCreators({ signal } = {}) {
  const { data } = await api.get(`${QUOTATIONS_PATH}/creators`, { signal });
  return Array.isArray(data?.data) ? data.data : [];
}

export function buildEligibleTicketParams(filters = {}) {
  const params = {};
  const branch = String(filters.branch || "").trim().toLowerCase();
  const quotationId = String(filters.quotation_id || "").trim();
  if (branch) params.branch = branch;
  if (quotationId) params.quotation_id = quotationId;
  return params;
}

export async function getServiceTicketSqAction(ticketId, { signal } = {}) {
  const { data } = await api.get(
    `${QUOTATIONS_PATH}/service-tickets/${encodeURIComponent(ticketId)}/sq`,
    { signal }
  );
  return data?.data || null;
}

export async function listEligibleServiceTickets(filters, { signal } = {}) {
  const { data } = await api.get(
    `${QUOTATIONS_PATH}/eligible-service-tickets`,
    { params: buildEligibleTicketParams(filters), signal }
  );
  return data;
}

export async function getQuotation(quotationId, { signal } = {}) {
  const { data } = await api.get(`${QUOTATIONS_PATH}/${encodeURIComponent(quotationId)}`, {
    signal,
  });
  return data?.data;
}

export async function createQuotation(payload) {
  const { data } = await api.post(QUOTATIONS_PATH, payload);
  return data;
}

export async function updateQuotation(quotationId, payload) {
  const { data } = await api.patch(
    `${QUOTATIONS_PATH}/${encodeURIComponent(quotationId)}`,
    payload
  );
  return data;
}

export async function deleteQuotation(quotationId, reason) {
  const { data } = await api.delete(
    `${QUOTATIONS_PATH}/${encodeURIComponent(quotationId)}`,
    { data: { reason } }
  );
  return data;
}

export async function listDeletedQuotations(filters, { signal } = {}) {
  const { data } = await api.get(`${QUOTATIONS_PATH}/deleted`, {
    params: buildQuotationHistoryParams(filters),
    signal,
  });
  return data;
}

export async function getDeletedQuotation(quotationId, { signal } = {}) {
  const { data } = await api.get(
    `${QUOTATIONS_PATH}/deleted/${encodeURIComponent(quotationId)}`,
    { signal }
  );
  return data?.data;
}

export async function getQuotationAttachment({ signal } = {}) {
  const { data } = await api.get(`${QUOTATIONS_PATH}/attachment`, { signal });
  return data?.data || null;
}

export async function putQuotationAttachment(file) {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.put(`${QUOTATIONS_PATH}/attachment`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data?.data || null;
}

export function quotationPdfFilename(
  contentDisposition,
  fallback = "Quotation.pdf"
) {
  const safeFilename = (value, defaultValue) => {
    const cleaned = String(value || "")
      .replace(/[\u0000-\u001f\u007f]/g, "")
      .split(/[\\/]/)
      .pop()
      ?.trim();
    return cleaned && cleaned !== "." && cleaned !== ".." ? cleaned : defaultValue;
  };
  const safeFallback = safeFilename(fallback, "Quotation.pdf");
  const header = String(contentDisposition || "");
  const extended = /filename\*\s*=\s*(?:"([^"]*)"|([^;\s]*))/i.exec(header);
  if (extended) {
    const encoded = extended[1] ?? extended[2] ?? "";
    const rfc5987 = /^[^']*'[^']*'(.*)$/.exec(encoded);
    const payload = rfc5987 ? rfc5987[1] : encoded;
    try {
      const decoded = safeFilename(decodeURIComponent(payload), "");
      if (decoded) return decoded;
    } catch {
      // Fall through to the ASCII filename supplied by the backend.
    }
  }
  const ascii = /filename\s*=\s*(?:"((?:[^"\\]|\\.)*)"|([^;\s]*))/i.exec(header);
  if (!ascii) return safeFallback;
  const value = (ascii[1] ?? ascii[2] ?? "").replace(/\\(["\\])/g, "$1");
  return safeFilename(value, safeFallback);
}

export async function generateQuotationPdf(
  quotationId,
  fallbackFilename = "Quotation.pdf"
) {
  const response = await api.post(
    `${QUOTATIONS_PATH}/${encodeURIComponent(quotationId)}/generate`,
    undefined,
    { responseType: "blob" }
  );
  return {
    blob: response.data,
    filename: quotationPdfFilename(
      response.headers?.["content-disposition"],
      fallbackFilename
    ),
  };
}

export function downloadQuotationPdf({ blob, filename }) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  try {
    link.href = url;
    link.download = quotationPdfFilename("", filename);
    document.body.appendChild(link);
    link.click();
  } finally {
    link.remove();
    URL.revokeObjectURL(url);
  }
}

export async function listQuotationItems({ signal, search = "" } = {}) {
  const q = String(search).trim();
  const { data } = await api.get("/items", {
    params: q ? { q } : {},
    signal,
  });
  return Array.isArray(data) ? data : [];
}
